import { BuyerUserRole } from '@choco-admin/types';
import React, { useState, useCallback } from 'react';

import {
  EntitySearchModal,
  type EntitySearchOption,
  type RoleOption,
} from '../../../../components/FormComponents';
import { useBuyerLinking } from '../../hooks/useBuyerLinking';
import { useBuyerSearch } from '../../hooks/useBuyerSearch';

import { BuyerSelectOption, ROLE_LABELS } from './types';

interface LinkBuyerModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  linkedBuyers: BuyerSelectOption[];
  onSuccess?: (newLinkedBuyer: BuyerSelectOption) => void;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: BuyerUserRole.ADMIN,
    label: ROLE_LABELS[BuyerUserRole.ADMIN],
    description: 'Full access to all buyer features',
  },
  {
    value: BuyerUserRole.STAFF,
    label: ROLE_LABELS[BuyerUserRole.STAFF],
    description: 'Standard user access',
  },
  {
    value: BuyerUserRole.CONTRIBUTOR,
    label: ROLE_LABELS[BuyerUserRole.CONTRIBUTOR],
    description: 'Limited access for external contributors',
  },
  {
    value: BuyerUserRole.SUPPORT,
    label: ROLE_LABELS[BuyerUserRole.SUPPORT],
    description: 'Support access for troubleshooting',
  },
];

export const LinkBuyerModal: React.FC<LinkBuyerModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  linkedBuyers,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);

  const {
    isLoading: searchLoading,
    options: searchResults,
    searchBuyers,
  } = useBuyerSearch();
  const { linkUserToBuyer, isLinking } = useBuyerLinking();

  // Filter out already linked buyers and convert to EntitySearchOption format
  const linkedBuyerIds = new Set(linkedBuyers.map((buyer) => buyer.value));
  const availableBuyers: EntitySearchOption[] = searchResults
    .filter((buyer) => !linkedBuyerIds.has(buyer.value))
    .map((buyer) => ({
      id: buyer.value,
      name: buyer.label,
      label: buyer.label,
      subtitle: buyer.metaData?.rolesEnabled ? 'Roles enabled' : undefined,
      metadata: buyer.metaData,
    }));

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim().length > 2) {
        searchBuyers(searchTerm.trim());
      }
    },
    [searchBuyers]
  );

  const handleConfirm = async (
    selectedEntity: EntitySearchOption,
    role?: string
  ) => {
    setError(null);

    // Convert back to BuyerSelectOption format
    const buyerWithRole: BuyerSelectOption = {
      value: selectedEntity.id,
      label: selectedEntity.label,
      metaData: {
        ...selectedEntity.metadata,
        buyerUserRole: role as BuyerUserRole,
      },
    };

    try {
      // If we have a non-empty userId, this is an edit flow - call the mutation
      if (userId && userId !== '') {
        await linkUserToBuyer(buyerWithRole, userId);
      }

      // Always call onSuccess with the selected buyer
      onSuccess?.(buyerWithRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link buyer');
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Link User to Buyer"
      searchPlaceholder="Search buyers by name or reference ID..."
      entityType="buyer"
      isSearching={searchLoading}
      searchResults={availableBuyers}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isLinking}
      roleOptions={ROLE_OPTIONS}
      defaultRole={BuyerUserRole.STAFF}
      error={error}
      onErrorDismiss={handleErrorDismiss}
    />
  );
};
